import { addDays, parse, subDays } from 'date-fns';
import { CreditCard, Info, AlertTriangle, Mail, Phone, Upload, Landmark } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import IbanField from '@/components/forms/iban-field';
import Select from '@/components/forms/select';
import UploadInline from '@/components/shared/upload-inline';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useChangeFractionnement } from '@/hooks/api/use-amendments';
import { useFeaturesDisabled } from '@/hooks/api/use-settings';
import { diffInDays, toDate } from '@/lib/dates';
import type { Contract } from '@/types/api/contract';

/** Valeurs du formulaire « Je passe en prélèvement ». */
interface FractionnementValues {
    fractionnement: string;
    createChrono: boolean;
    iban?: string;
    files?: File;
    [key: string]: unknown;
}

/** Hors de la fenêtre [échéance − 60 j ; échéance + 15 j] (bornes incluses), un chrono est créé. */
const isOutsideEcheanceWindow = (dateEcheance?: string) => {
    const echeance = toDate(dateEcheance);
    const now = Date.now();
    return !(now >= subDays(echeance, 60).getTime() && now <= addDays(echeance, 15).getTime());
};

export interface FractionnementProps {
    contract: Contract;
}

const Fractionnement = ({ contract }: FractionnementProps) => {
    const souscripteur = contract.souscripteur || {};
    const dateEcheance = contract.situation?.dateEcheance;
    const id_contrat = contract.id;
    /*
     * Contrat au chèque : changer de fractionnement, c'est passer au prélèvement, et le RIB est
     * obligatoire (le Pass refuse la demande sans IBAN ni RIB). Déjà prélevé, seul le rythme change :
     * le mandat en place suffit.
     */
    const ribObligatoire = !contract.prelevement;
    const initialValues: FractionnementValues = {
        fractionnement: "",
        createChrono: isOutsideEcheanceWindow(dateEcheance)
    };
    const { mutate: postChangeFractionnement, isPending: loading } = useChangeFractionnement();
    const featuresDisabled = useFeaturesDisabled();
    const [createChrono, setCreateChrono] = useState<boolean | null>(null)
    const anneeEnCours = new Date().getFullYear();
    const date = parse(`${dateEcheance}/${anneeEnCours}`, 'dd/MM/yyyy', new Date());
    const navigate = useNavigate();

    const methods = useForm<FractionnementValues>({
        defaultValues: initialValues,
        mode: 'onSubmit',
    });

    const { handleSubmit, formState: { errors }, watch, setValue } = methods;
    const values = watch();

    useEffect(() => {
        if (diffInDays(new Date(), date) >= -60 && diffInDays(new Date(), date) <= 15){
          setCreateChrono(false)
        } else {
          setCreateChrono(true)
        }
    }, [])

    return (
        <div className="space-y-6">
            {/* Hero */}
            <div className="relative overflow-hidden rounded-2xl bg-primary p-8 text-white mt-12">
                <div className="absolute -top-1/2 -right-[10%] w-[400px] h-[400px] bg-white/10 rounded-full" />
                <div className="relative z-10">
                    <p className="text-sm opacity-90 mb-1">Mon contrat</p>
                    <h1 className="text-3xl font-semibold flex items-center gap-3">
                        <CreditCard className="w-8 h-8" />
                        Je passe en prélèvement
                    </h1>
                </div>
            </div>

            {/* Fonctionnalité indisponible */}
            {featuresDisabled && (
                <Alert variant="destructive">
                    <AlertTriangle className="w-4 h-4" />
                    <AlertTitle>Fonctionnalité indisponible</AlertTitle>
                    <AlertDescription>
                        Cette fonctionnalité est indisponible pour le moment : prélèvement en cours.
                    </AlertDescription>
                </Alert>
            )}

            {/* Vérification coordonnées */}
            <Alert variant="warning">
                <Info className="w-4 h-4" />
                <AlertDescription>
                    <div className="space-y-1">
                        <p className="flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5 shrink-0" />
                            Vérifiez votre email : <span className="font-semibold">{souscripteur.email}</span>
                        </p>
                        <p className="flex items-center gap-2">
                            <Phone className="w-3.5 h-3.5 shrink-0" />
                            Vérifiez votre téléphone : <span className="font-semibold">{souscripteur.phone}</span>
                        </p>
                        <p className="text-xs mt-1">
                            Si ces informations sont incorrectes, modifiez-les dans <Link to="/mon-profil" className="text-primary font-medium underline underline-offset-2">Mon profil</Link>.
                        </p>
                    </div>
                </AlertDescription>
            </Alert>

            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(form => postChangeFractionnement({createChrono, contractId: id_contrat, form, successFunction: () => navigate(`/mes-contrats/mon-contrat/${id_contrat}`)}))} className="space-y-6">
                    {/* Choix du fractionnement */}
                    <Card>
                        <CardHeader className="pb-6 border-b">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <CreditCard className="w-5 h-5 text-primary" />
                                Mode de prélèvement
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <div className="max-w-md">
                                <Select
                                    required
                                    name={"fractionnement"}
                                    label={'Nouveau fractionnement'}
                                    error={errors.fractionnement?.message}
                                    value={values.fractionnement}
                                >
                                    <option value={''}>Sélectionnez un fractionnement</option>
                                    <option value={"A"}>Fractionnement annuel en prélèvement</option>
                                    <option value={"M"}>Fractionnement mensuel en prélèvement</option>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Upload RIB / IBAN */}
                    <Card>
                        <CardHeader className="pb-6 border-b">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Upload className="w-5 h-5 text-primary" />
                                {!createChrono ? 'Votre RIB' : 'Votre IBAN'}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-5">
                            <UploadInline
                                setValue={setValue}
                                code={`files`}
                                label={!createChrono ? "Importer votre RIB" : "Importer votre IBAN"}
                                required={ribObligatoire}
                            />

                            {!createChrono && (
                                <div className="pt-2">
                                    <p className="text-sm text-muted-foreground mb-6 flex items-center gap-2">
                                        <Landmark className="w-4 h-4 shrink-0" />
                                        Renseignez votre IBAN pour récupérer automatiquement le BIC et la domiciliation.
                                    </p>
                                    <IbanField />
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Action */}
                    <Button
                        type="submit"
                        className="w-full cursor-pointer"
                        disabled={featuresDisabled}
                        loading={loading}
                        loadingText="Mise à jour en cours..."
                    >
                        <CreditCard className="w-4 h-4" />
                        Mettre à jour le prélèvement
                    </Button>
                </form>
            </FormProvider>
        </div>
    );
};

export default Fractionnement;
